import * as React from "react"
import { sourceControlProviderLabels } from "@modules/sourceControlProvider"
import { interpolateMessage } from "@modules/locales"
import { SourceControl, SourceControlProvider } from "@modules/graphql/types"
import { createSite as text } from "@modules/locales/default.js"
import { Button, GitHubIcon, Text, ThemeCss } from "gatsby-interface"
import { processSignupAttempt } from "@modules/auth/utils"
import { getUserId } from "@modules/analytics/utils"
import {
  getPathToCreateSite,
  getPathToProvisionSite,
  isGithubOrGithubEnterprise,
  mapProviderToLoginOption,
} from "@modules/site/create/shared/utils"
import { flexGrowCss } from "../../SiteCreationTypePicker"
import { useAvailableSourceProvidersWithVcsConfigQuery } from "../../Import/queries.generated"

const buttonCss: ThemeCss = theme => ({
  fontSize: `inherit`,
  textDecoration: `underline`,
  padding: `0`,
  marginRight: theme.space[2],
})

export function NoInstallations({
  hasClosedWindow,
  popInstallationWindow,
  provider,
  fromCreateSiteFlow = false,
  organizationId,
  vcsConfigId,
}: {
  hasClosedWindow: boolean
  popInstallationWindow: (trackName: string, trackOpts: {}) => void
  provider: SourceControlProvider
  fromCreateSiteFlow?: boolean
  organizationId?: string
  vcsConfigId?: string
}) {
  const { data: sourceProviders, loading } =
    useAvailableSourceProvidersWithVcsConfigQuery({
      variables: {
        workspaceId: organizationId,
      },
      fetchPolicy: "network-only",
      skip: fromCreateSiteFlow || provider !== SourceControlProvider.Github,
    })

  const isGithubConnected = () => {
    if (provider !== SourceControlProvider.Github) {
      return false
    }
    const githubConnection: SourceControl | undefined =
      sourceProviders?.availableSourceProvidersWithVcsConfig?.find(
        connection => connection.source === SourceControlProvider.Github
      )
    return githubConnection?.connected
  }

  const githubConnected = React.useMemo(isGithubConnected, [
    sourceProviders?.availableSourceProvidersWithVcsConfig,
  ])

  if (loading) {
    return null
  }

  if (hasClosedWindow || fromCreateSiteFlow || githubConnected) {
    const providerLabel = sourceControlProviderLabels[provider]
    const openedSourceControlMessage = text.messages.addNewGithubInstallation

    const message = hasClosedWindow
      ? interpolateMessage<"provider">(text.messages.pendingSourceControlAuth, {
          provider: providerLabel,
        })
      : interpolateMessage<"provider">(openedSourceControlMessage, {
          provider: providerLabel,
        })

    return (
      <div
        css={theme => [
          flexGrowCss(theme),
          {
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            backgroundColor: theme.colors.grey[5],
            alignItems: "center",
            width: "100%",
            height: "100%",
            padding: `${theme.space[5]} ${theme.space[12]}`,
            margin: "0 auto",
          },
        ]}
      >
        <Text
          css={theme => ({
            textAlign: "center",
            fontSize: theme.fontSizes[2],
          })}
        >
          {message}
        </Text>
        <Text
          css={theme => ({
            textAlign: "center",
            fontSize: theme.fontSizes[2],
          })}
        >
          <Button
            variant="GHOST"
            css={buttonCss}
            onClick={() =>
              popInstallationWindow("Add it", {
                uiSource: `Repository Selection`,
              })
            }
          >
            Click here to add an installation
          </Button>
        </Text>

        <Text
          css={theme => ({
            textAlign: "center",
            fontSize: theme.fontSizes[2],
          })}
        >
          Having trouble with your installations? Try authorizing your
          connection to refresh your settings{" "}
        </Text>
        <Button
          variant="GHOST"
          css={buttonCss}
          onClick={() => {
            // we should only need this for github or github enterprise
            if (isGithubOrGithubEnterprise(provider)) {
              const pathFromAuthRedirect = fromCreateSiteFlow
                ? getPathToCreateSite(organizationId)
                : getPathToProvisionSite(organizationId)
              processSignupAttempt(
                mapProviderToLoginOption[provider],
                {
                  csrfToken: getUserId(),
                  attachProvider: true,
                  errorRedirectUrl: pathFromAuthRedirect,
                  loginRedirectUrl: pathFromAuthRedirect,
                  ...(vcsConfigId && {
                    enterpriseVCSConfigId: vcsConfigId,
                  }),
                },
                {}
              )
            }
          }}
        >
          Click here to authorize
        </Button>
      </div>
    )
  }

  return (
    <Button
      css={theme => ({
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: theme.space[15],
        height: theme.space[15],
        border: `1px solid ${theme.colors.grey[10]}`,
        borderRadius: theme.radii[3],
        boxShadow: theme.shadows.raised,
        background: theme.colors.white,
        color: theme.colors.black,
        "&:hover": {
          cursor: "pointer",
          background: theme.colors.white,
        },
        "&:focus": {
          background: theme.colors.white,
        },
      })}
      onClick={() => {
        processSignupAttempt(
          mapProviderToLoginOption[provider],
          {
            csrfToken: getUserId(),
            attachProvider: true,
            errorRedirectUrl: getPathToProvisionSite(organizationId),
            loginRedirectUrl: getPathToProvisionSite(organizationId),
          },
          {}
        )
      }}
    >
      <GitHubIcon />
      <Text
        css={theme => ({
          color: theme.colors.black,
          fontSize: theme.fontSizes[1],
        })}
        noMarginBottom
      >
        GitHub
      </Text>
    </Button>
  )
}
